import { useEffect, useRef } from "react";
import { m } from "framer-motion";
import { Star } from "lucide-react";
import { siteConfig } from "@/lib/site-config";

/**
 * Reputation Hub review widget — preserves original embed height,
 * no clipping, no scrollbars, no forced max-height.
 */
const ReviewWidget = () => {
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const scriptId = "reputation-hub-review-script";
    if (!document.getElementById(scriptId)) {
      const s = document.createElement("script");
      s.id = scriptId;
      s.src = siteConfig.widgets.reviewScriptSrc;
      s.type = "text/javascript";
      s.async = true;
      document.body.appendChild(s);
    }
  }, []);

  return (
    <section className="section-padding section-gradient-soft">
      <div className="container-max">
        <div className="text-center mb-10 md:mb-12">
          <m.span
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="inline-block text-primary text-sm font-bold uppercase tracking-widest mb-3"
          >
            Customer Reviews
          </m.span>
          <m.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="font-heading text-4xl md:text-5xl lg:text-6xl text-foreground mb-4 tracking-tight font-bold"
          >
            What Our <span className="text-gradient-primary">Clients</span> Say
          </m.h2>
          <m.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="flex items-center justify-center gap-1 mb-3"
          >
            {[...Array(5)].map((_, i) => (
              <Star key={i} className="h-5 w-5 fill-secondary text-secondary" />
            ))}
          </m.div>
          <p className="text-muted-foreground max-w-2xl mx-auto text-base md:text-lg">
            Real reviews from real Middle Tennessee customers.
          </p>
        </div>

        <m.div
          ref={wrapRef}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="review-widget-wrap max-w-5xl mx-auto"
        >
          <iframe
            className="lc_reviews_widget"
            src={siteConfig.widgets.reviewIframeSrc}
            loading="lazy"
            scrolling="no"
            style={{ minWidth: "100%", width: "100%", border: "none", display: "block" }}
            title="Customer reviews"
          />
        </m.div>
      </div>
    </section>
  );
};

export default ReviewWidget;
